import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import debounce from 'lodash/debounce';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { GenericLinkDirective } from '@core/jss-utils';

import { ButtonSize, ButtonTheme, ButtonVariant } from '../../models';

@Component({
  selector: 'wl-partial-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  standalone: true,
  imports: [NgTemplateOutlet, NgClass, GenericLinkDirective, FaIconComponent],
})
export class ButtonComponent {
  // States
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() disabled = false;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() loading = false;

  // Behaviour
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() role?: 'link' | 'button';
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() type: 'button' | 'reset' | 'submit' = 'submit';
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() href?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() target: '_self' | '_blank' | '_parent' | '_top' = '_self';
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rel?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() anchor?: string;
  /**
   * With `debounceClick = true`, the (click) emitter will be capped to 1 per 250ms.
   * `debounceClick` can also be set to a number to modify the default 250ms.
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() debounceClick: number | boolean = false;

  // Accessibility
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() ariaLabel?: string;
  @HostBinding('[attr.aria-disabled]') get isDisabled() {
    return this.disabled || this.loading;
  }

  // Styles
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() size: ButtonSize = 'default';
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() theme: ButtonTheme = 'primary';
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() context: ButtonVariant = 'default';
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() fullWidth = false;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() block = false;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() active = false;

  @Output() clicked = new EventEmitter<MouseEvent>();

  @ViewChild('buttonElement') buttonElement?: ElementRef<HTMLButtonElement>;

  private get clickDebounceTime() {
    return typeof this.debounceClick === 'number' ? this.debounceClick : 500;
  }

  /**
   * With lodash debounce it eliminates the delay from click to action. With `leading: true` it will
   * emit `click` immediately, but only a second time when clicked again after 250ms.
   */
  private readonly clickDebouncer = debounce(
    (event: MouseEvent) => this.clicked.emit(event),
    this.clickDebounceTime,
    { leading: true, trailing: false }
  );

  get ariaLabelFallback(): string | undefined {
    return this.buttonElement?.nativeElement.innerText;
  }

  /**
   * Workaround to make buttons work with the
   * currently used bootstrap classes for ED restyling.
   * When moving to component library, remove this coupling with bootstrap
   * and base styles on component inputs solely.
   */
  get computedStyle() {
    const inferredContext = this.context === 'default' ? '' : this.context;
    const inferredClass = `btn-${inferredContext ? inferredContext + '-' : ''}${
      this.theme
    }`;

    return `btn ${inferredClass}`;
  }

  onClick(event: MouseEvent): void {
    if (this.debounceClick) {
      this.clickDebouncer(event);
    } else {
      this.clicked.emit(event);
    }
  }
}
